/**
 * Navigation sidebar component for the Account section of the dashboard.
 * Provides navigation links to different account management features such as
 * profile settings, membership management, and communication preferences.
 *
 * It is intended to be added to the right side of the Account (user's) section.
 * You can see its usage in the 'index' page of the Profile component and how any
 * sub-component rendered based on the route path will have this component on
 * the right.
 */

import { Box, VStack, Button, ButtonProps } from '@chakra-ui/react'
import { useHistory } from 'react-router-dom'
import { RoutePaths } from 'containers/Core/Routes'
import { useFlags } from 'launchdarkly-react-client-sdk'
import useStripePortal from 'hooks/useStripePortal'
import { useMemo } from 'react'

interface AccountNavbarProps {
  currentPath: string
}

// Helper hook for managing active link aspect. Mostly to make the active link a bit faded.
const highlightActiveLink = (
  currentPath: string,
  linkPath: string
): Pick<ButtonProps, 'opacity' | 'aria-current'> => {
  const isActive = useMemo(() => currentPath === linkPath, [
    currentPath,
    linkPath,
  ])
  return {
    opacity: isActive ? 0.5 : 1,
    'aria-current': isActive ? ('page' as const) : undefined,
  }
}

const AccountNavbar: React.FC<AccountNavbarProps> = ({ currentPath }) => {
  const history = useHistory()
  const { selfServiceCancellation, unsubscribeFromEmails } = useFlags()
  const { stripePortalLink } = useStripePortal()

  return (
    <Box>
      <VStack align="flex-start" spacing={5}>
        {selfServiceCancellation && (
          <Button
            variant="link"
            onClick={() => {
              history.push(RoutePaths.MANAGE_MEMBERSHIP)
            }}
            color="var(--blue-dark)"
            fontWeight={400}
            {...highlightActiveLink(currentPath, RoutePaths.MANAGE_MEMBERSHIP)}
          >
            Manage account
          </Button>
        )}
        <Button
          variant="link"
          onClick={() => {
            history.push(RoutePaths.DASHBOARD_PROFILE)
          }}
          color="var(--blue-dark)"
          fontWeight={400}
          {...highlightActiveLink(currentPath, RoutePaths.DASHBOARD_PROFILE)}
        >
          Edit profile
        </Button>
        {unsubscribeFromEmails && (
          <Button
            variant="link"
            onClick={() => {
              history.push(RoutePaths.MANAGE_COMMUNICATION_PREFERENCES)
            }}
            color="var(--blue-dark)"
            fontWeight={400}
            {...highlightActiveLink(
              currentPath,
              RoutePaths.MANAGE_COMMUNICATION_PREFERENCES
            )}
          >
            Communication preferences
          </Button>
        )}
        <a href={stripePortalLink} target="_blank" rel="noreferrer">
          <Button variant="link" color="var(--blue-dark)" fontWeight={400}>
            My billing information
          </Button>
        </a>
      </VStack>
    </Box>
  )
}

export default AccountNavbar
